import moment from 'moment'
import { getLevelIdForMethod } from '~/composables/planning-helpers'
import { MethodologyEnum, type PlanPosItem } from '~/protos/gen/stcs_comp_plan_cfg.entity'
import { StcsDisplayValueTypeEnum, StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity.ts'
import { useCtxStore } from '~/stores/ctx.store'
import { createPlanningViewStore } from '~/stores/planningview.store'
import { getInvertSumValueFromRowList } from './planning-helpers'
import { createValidInputManualMap } from './unified-edit-api'

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function createMonthlyGrowthPosEntry(row: PlanningRowData, isSubPosition = false, isStatusUpdate = false, parentRow?: PlanningRowData): PlanPosItem {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  const list = row.fstmt_type === StcsFstmtEnum.SFSE_PNL ? planningStore.selectedPlanState?.pnlList : planningStore.selectedPlanState?.bsList || []
  const invertSumValue = row.options?.invert_sum_value as number
  const parentRowId = row.path_level[row.path_level.length - 2]

  // Find direct child with rowClass='selected-method-row' by checking parent row_id
  // Use second-to-last element in path_level to identify parent (works with any filter state)
  const BaseRow = list?.find((r) => {
    if (r.rowClass !== 'selected-method-row') return false
    const parentRowId = r.path_level[r.path_level.length - 2]
    return parentRowId === row.row_id
  }) as PlanningRowData

  const inputMap = createValidInputManualMap(BaseRow)

  const basAmountKey = moment(selectedPlan?.fpna_begin_date_string, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')

  // Get pos_res_plan_map from cache only when isStatusUpdate is true
  let pos_res_plan_map = {}
  if (isStatusUpdate && selectedPlan) {
    const IS_PNL = row.fstmt_type === StcsFstmtEnum.SFSE_PNL
    const mapKey = IS_PNL ? 'pnl_map' : 'bs_map'

    if (isSubPosition && parentRow) {
      // For sub-positions, look in parent's formula_based.sub_pos_map
      const grandParentRowId = parentRow.path_level[parentRow.path_level.length - 2]
      pos_res_plan_map =
        planningStore.planConfigCache[selectedPlan.id || '']?.stcs_comp_plan_cfg?.[mapKey]?.[grandParentRowId]?.pos_map?.[parentRow.row_id]?.formula_based?.sub_pos_map?.[row.row_id]?.monthly_growth_rate?.pos_res_plan_map || {}
    } else {
      // For main positions, look in pos_map
      pos_res_plan_map = planningStore.planConfigCache[selectedPlan.id || '']?.stcs_comp_plan_cfg?.[mapKey]?.[parentRowId]?.pos_map?.[row.row_id]?.monthly_growth_rate?.pos_res_plan_map || {}
    }
  }

  // Extract base amount value from inputMap using basAmountKey
  const baseAmountValue = inputMap[basAmountKey]?.manual_number_input || 0

  // Remove the base amount key from inputMap so it doesn't get passed to pos_input_map
  if (inputMap[basAmountKey] !== undefined) {
    delete inputMap[basAmountKey]
  }

  // Multiply each entry by invert_sum_value
  let invertedInputMap = Object.fromEntries(Object.entries(inputMap).map(([date, value]) => [date, { ...value, manual_number_input: value.manual_number_input * invertSumValue }]))

  if (isSubPosition) {
    switch (row.outputType) {
      case StcsDisplayValueTypeEnum.SDVTE_ABSOLUTE:
      case StcsDisplayValueTypeEnum.SDVTE_PERCENTAGE:
        // For absolute/percentage types, multiply by invertSumValue again
        invertedInputMap = Object.fromEntries(Object.entries(invertedInputMap).map(([date, value]) => [date, { ...value, manual_number_input: value.manual_number_input * invertSumValue }]))
        break

      default:
        break
    }
  }

  return {
    pos_logic_type: MethodologyEnum.MONTHLY_GROWTH_RATE,
    fstmt_type: row.fstmt_type,
    display_value_type: row.outputType || StcsDisplayValueTypeEnum.SDVTE_CURRENCY,
    monthly_growth_rate: {
      pos_input_map: invertedInputMap,
      pos_res_plan_map: pos_res_plan_map, // Populated from cache when isStatusUpdate is true
      pos_input_base_amount: baseAmountValue,
    },
    is_struct_item: false, // Handle by the backend only
    pos_index: 0,
    status: undefined, // Handle by the backend only
  }
}

export const handleMonthlyGrowthPositions = (positionKey: string, planPosition: PlanPosItem, isSubPosition = false) => {
  const selectedMethodRowKey = `${positionKey}_${getLevelIdForMethod(MethodologyEnum.MONTHLY_GROWTH_RATE)}`

  // planning store
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  if (!selectedPlan) return

  const IS_PNL = planPosition.fstmt_type === StcsFstmtEnum.SFSE_PNL

  // Get invert_sum_value by traversing the row hierarchy
  const rowList = IS_PNL ? selectedPlan.pnlList : selectedPlan.bsList
  const invertSumValue = getInvertSumValueFromRowList(positionKey, rowList)

  // values map from plan store
  const valuesMap = IS_PNL ? selectedPlan.valuesMapPnl || {} : selectedPlan.valuesMapBs || {}

  // Extract the base amount and input map from the plan position
  const baseAmount = planPosition.monthly_growth_rate?.pos_input_base_amount || 0

  const inputMap = Object.fromEntries(Object.entries(planPosition.monthly_growth_rate?.pos_input_map || {}).map(([date, { manual_number_input }]) => [date, manual_number_input]))
  // Sort the input map by date keys to ensure chronological order
  const sortedInputEntries = Object.entries(inputMap).sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
  const basAmountKey = moment(selectedPlan?.fpna_begin_date_string, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')

  valuesMap[selectedMethodRowKey] = {
    values_map: {
      [basAmountKey]: baseAmount,
      ...Object.fromEntries(sortedInputEntries.map(([date, value]) => [date, (value || 0) * invertSumValue])),
    },
  }

  if (isSubPosition) {
    const positionValues = planPosition.monthly_growth_rate?.pos_res_plan_map || {}
    const sortedPositionEntries = Object.entries(positionValues).sort(([dateA], [dateB]) => dateA.localeCompare(dateB))

    let shouldInvert: boolean

    switch (planPosition.display_value_type) {
      case StcsDisplayValueTypeEnum.SDVTE_ABSOLUTE:
      case StcsDisplayValueTypeEnum.SDVTE_PERCENTAGE:
        shouldInvert = false
        break
      default:
        shouldInvert = true
        break
    }

    valuesMap[selectedMethodRowKey] = {
      values_map: {
        [basAmountKey]: baseAmount,
        ...Object.fromEntries(sortedInputEntries.map(([date, value]) => [date, shouldInvert ? (value || 0) * invertSumValue : value])),
      },
    }

    valuesMap[positionKey] = {
      values_map: Object.fromEntries(sortedPositionEntries.map(([date, value]) => [date, value || 0])),
    }
  }

  if (IS_PNL) {
    selectedPlan.valuesMapPnl = valuesMap
  } else {
    selectedPlan.valuesMapBs = valuesMap
  }
}
